import { Delete } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { SdkStatus } from "../hooks/useATClient";
import { BottomSheet } from "../components/BottomSheet";
import { cn } from "../lib/cn";

// ─── Key definitions ───────────────────────────────────────────────────────────

const KEYS = [
  { digit: "1", sub: "" },
  { digit: "2", sub: "ABC" },
  { digit: "3", sub: "DEF" },
  { digit: "4", sub: "GHI" },
  { digit: "5", sub: "JKL" },
  { digit: "6", sub: "MNO" },
  { digit: "7", sub: "PQRS" },
  { digit: "8", sub: "TUV" },
  { digit: "9", sub: "WXYZ" },
  { digit: "*", sub: "" },
  { digit: "0", sub: "+" },
  { digit: "#", sub: "" },
] as const;

// ─── Country list (Nigeria only for now) ──────────────────────────────────────

export const COUNTRIES = [{ code: "+234", iso: "NG", name: "Nigeria" }] as const;

function flagEmoji(iso: string): string {
  return iso
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

// ─── Local time ────────────────────────────────────────────────────────────────

function useLocalTime(): string {
  const fmt = () =>
    new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  const [time, setTime] = useState(fmt);
  useEffect(() => {
    const id = setInterval(() => setTime(fmt()), 30_000);
    return () => clearInterval(id);
  }, []);
  const offset = -new Date().getTimezoneOffset() / 60;
  const sign = offset >= 0 ? "+" : "-";
  return `Local time: ${time} (GMT${sign}${Math.abs(offset)})`;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface DialpadScreenProps {
  sdkStatus: SdkStatus;
  value: string;
  onChange: (v: string) => void;
  countryCode: string;
  onCountryCodeChange: (code: string) => void;
  isActive: boolean;
}

export function DialpadScreen({
  sdkStatus,
  value,
  onChange,
  countryCode,
  onCountryCodeChange,
  isActive,
}: DialpadScreenProps) {
  const [countrySheetOpen, setCountrySheetOpen] = useState(false);
  const localTime = useLocalTime();
  const inputRef = useRef<HTMLInputElement>(null);

  const isInCall = sdkStatus === "ringing" || sdkStatus === "incall";

  const selectedCountry = COUNTRIES.find((c) => c.code === countryCode) ?? COUNTRIES[0];

  const stateRef = useRef({ value, isInCall });
  stateRef.current = { value, isInCall };

  // Append a digit — always at end of value, then re-focus input at end.
  const append = useCallback(
    (char: string) => {
      if (stateRef.current.isInCall) return;
      const newValue = stateRef.current.value + char;
      onChange(newValue);
      requestAnimationFrame(() => {
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        input.setSelectionRange(newValue.length, newValue.length);
      });
    },
    [onChange],
  );

  // Backspace button: respects cursor position when the input is focused.
  const backspace = useCallback(() => {
    const input = inputRef.current;
    const v = stateRef.current.value;
    if (!v) return;

    if (input && document.activeElement === input) {
      const start = input.selectionStart ?? v.length;
      const end = input.selectionEnd ?? v.length;
      // Delete selection if there is one.
      if (start !== end) {
        const newValue = v.slice(0, start) + v.slice(end);
        onChange(newValue);
        requestAnimationFrame(() => input.setSelectionRange(start, start));
        return;
      }
      // Delete char before cursor.
      if (start > 0) {
        const newValue = v.slice(0, start - 1) + v.slice(start);
        onChange(newValue);
        requestAnimationFrame(() => input.setSelectionRange(start - 1, start - 1));
        return;
      }
      return;
    }
    // Fallback: remove last char.
    onChange(v.slice(0, -1));
  }, [onChange]);

  // Auto-focus the input when this tab is active and not in a call.
  useEffect(() => {
    if (!isActive || isInCall) return;
    const id = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(id);
  }, [isActive, isInCall]);

  // Global keydown handler: captures keys typed outside the input and routes
  // them in, so users can just start typing on the dialpad screen.
  useEffect(() => {
    if (!isActive) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      // Let the input handle its own events when focused.
      if (target === inputRef.current) return;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;
      const { isInCall: ic } = stateRef.current;
      if (/^[0-9*#]$/.test(e.key)) {
        if (!ic) {
          e.preventDefault();
          onChange(stateRef.current.value + e.key);
          requestAnimationFrame(() => inputRef.current?.focus());
        }
      } else if (e.key === "Backspace") {
        e.preventDefault();
        const v = stateRef.current.value;
        onChange(v.slice(0, -1));
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isActive, onChange]);

  return (
    <div className="flex flex-1 select-none flex-col overflow-hidden">
      {/* ── Grey shell area ────────────────────────────────────────────────
       * This is the visible portion of the grey #root background.
       * Local time sits at the bottom of this space, just above the card.
       */}
      <div className="flex flex-1 items-end justify-center px-6 pb-4">
        <p className="text-xs text-muted-foreground">{localTime}</p>
      </div>

      {/* ── Floating dialpad card ──────────────────────────────────────────
       * Fully rounded on all sides with horizontal + bottom margin so it
       * sits as a distinct white card inside the grey shell — matching the
       * reference design. NOT a bottom sheet (no rounded-top-only).
       */}
      <div className="mx-3 mb-3 overflow-hidden rounded-2xl bg-card shadow-sm">
        {/* Country chip + number display ── */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-3">
          {/* Country code chip — tappable */}
          <button
            onClick={() => setCountrySheetOpen(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-muted px-3 py-1.5 text-sm font-semibold text-foreground active:scale-95"
          >
            <span className="text-base leading-none">{flagEmoji(selectedCountry.iso)}</span>
            <span>{selectedCountry.code}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3 text-muted-foreground"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Vertical divider */}
          <div className="h-5 w-px shrink-0 bg-border" />

          {/* Number input — real <input> for cursor positioning, selection, etc. */}
          <input
            ref={inputRef}
            type="tel"
            inputMode="none"
            value={value}
            onChange={(e) => {
              // Filter to only characters valid in a phone number.
              const filtered = e.target.value.replace(/[^0-9*#+]/g, "");
              onChange(filtered);
            }}
            placeholder="Enter number"
            disabled={isInCall}
            className={cn(
              "min-w-0 flex-1 bg-transparent outline-none caret-primary",
              value
                ? "text-[26px] font-light tracking-[0.04em] text-foreground"
                : "text-sm font-normal text-muted-foreground/50",
              isInCall && "cursor-not-allowed",
            )}
          />
        </div>

        {/* Thin separator between display and keypad */}
        <div className="mx-4 h-px bg-border/60" />

        {/* ── Keypad grid ────────────────────────────────────────────────── */}
        <div className="grid grid-cols-3 px-2 pt-2">
          {KEYS.map(({ digit, sub }) => (
            <button
              key={digit}
              onClick={() => append(digit)}
              disabled={isInCall}
              className={cn(
                "flex flex-col items-center justify-center rounded-xl py-[14px] transition-colors",
                isInCall
                  ? "cursor-not-allowed opacity-30"
                  : "cursor-pointer hover:bg-muted/60 active:bg-muted",
              )}
            >
              <span className="text-[24px] font-light leading-none text-foreground">{digit}</span>
              {sub && (
                <span className="mt-[4px] text-[9px] font-semibold tracking-[0.18em] text-muted-foreground">
                  {sub}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Backspace row ──────────────────────────────────────────────
         * Right column only — mirrors the reference design where ⌫ sits
         * below the # key with the left two cells empty.
         */}
        <div className="grid grid-cols-3 px-2 pb-3">
          <div />
          <div />
          <button
            onClick={backspace}
            disabled={!value}
            aria-label="Backspace"
            className={cn(
              "flex items-center justify-center rounded-xl py-3 transition-colors",
              value
                ? "text-muted-foreground hover:bg-muted/60 active:bg-muted active:scale-90"
                : "cursor-not-allowed text-muted-foreground/20",
            )}
          >
            <Delete className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* ── Country picker bottom sheet ──────────────────────────────────── */}
      <BottomSheet
        open={countrySheetOpen}
        onClose={() => setCountrySheetOpen(false)}
        title="Select country code"
      >
        <ul className="pb-6">
          {COUNTRIES.map((country) => {
            const isSelected = country.code === countryCode;
            return (
              <li key={country.code}>
                <button
                  onClick={() => {
                    onCountryCodeChange(country.code);
                    setCountrySheetOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-muted/60 active:bg-muted",
                    isSelected && "bg-primary/5",
                  )}
                >
                  <span className="text-2xl leading-none">{flagEmoji(country.iso)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">{country.name}</p>
                    <p className="text-xs text-muted-foreground">{country.code}</p>
                  </div>
                  {isSelected && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 shrink-0 text-primary"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </BottomSheet>
    </div>
  );
}
